import ActionButton from '@/components/buttons/ActionButton.tsx';
import React, { useEffect } from 'react';
import ModalContent from '@/components/modal/ModalContent.tsx';
import TwoInputsRow from '@/components/form/ui/TwoInputsRow.tsx';
import { TextInput } from '@/components/form/TextInput.tsx';
import SingleSelectInput from '@/components/form/SingleSelectInput';
import CheckboxInput from '@/components/form/CheckboxInput.tsx';
import ToggleInput from '@/components/form/ToggleInput.tsx';
import { ValueInput } from '@/components/form/ValueInput.tsx';
import AgeRestrictionPriceForm from '@/features/dashboard/tourOptions/components/AgeRestrictionPriceForm.tsx';
import {
  ParticipantLimitMode,
  participantLimitModesForTourOption,
  participantLimitModesForTourOptionWithAge,
} from '@/features/dashboard/tourOptions/types/ParticipantLimitMode.ts';
import TourVariantForm from '@/features/dashboard/tourOptions/components/TourVariantForm.tsx';
import { Plus } from 'lucide-react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { TourOptionFormData } from '@/features/dashboard/tourOptions/schemas/tourOptionSchema.ts';
import { currencySelectOptions } from '@/features/dashboard/paymentConfig/types/Currency.utils.ts';
import { TourOptionUsageDto } from '@/features/dashboard/tourOptions/types/TourOptionUsageDto.ts';

const emptyVariant = {
  name: '',
  price: 0,
  isPriceByAge: false,
  isVariantOnlyForDefinedAgeRanges: false,
  participantLimitMode: ParticipantLimitMode.NONE,
  ageRestrictionPrices: [],
};

const TourOptionsForm = ({
  isEdit = false,
  usage,
}: {
  isEdit?: boolean;
  usage?: TourOptionUsageDto;
}) => {
  const {
    register,
    control,
    formState: { errors },
    setValue,
    watch,
  } = useFormContext<TourOptionFormData>();

  const hasVariantsValue = watch('hasVariants');
  const currencyValue = watch('currency');
  const isPriceByAgeValue = watch('isPriceByAge');
  const participationLimitModeValue = watch('participantLimitMode');

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'variants',
  });

  const ageRestrictionPricesFieldArray = useFieldArray({
    control,
    name: 'ageRestrictionPrices',
  });

  useEffect(() => {
    if (!hasVariantsValue) {
      setValue('variants', []);
    } else if (fields.length === 0) {
      append([emptyVariant, emptyVariant]);
      setValue('isPriceByAge', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasVariantsValue]);

  useEffect(() => {
    if (participationLimitModeValue === ParticipantLimitMode.NONE) {
      setValue('participantLimit', undefined);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participationLimitModeValue]);

  useEffect(() => {
    if (!isPriceByAgeValue) {
      setValue('ageRestrictionPrices', []);
    } else if (ageRestrictionPricesFieldArray.fields.length === 0) {
      ageRestrictionPricesFieldArray.append({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPriceByAgeValue]);

  // Dynamic limit options based on variants and age pricing
  const getLimitOptions = () => {
    if (hasVariantsValue) {
      // With variants: use standard options
      return participantLimitModesForTourOption;
    } else if (isPriceByAgeValue) {
      // No variants + age pricing: include PER_PRICE option
      return participantLimitModesForTourOptionWithAge;
    } else {
      // No variants + no age pricing: standard options
      return participantLimitModesForTourOption;
    }
  };

  const limitOptions = getLimitOptions();

  // Wiersze cennika trzymane w rezerwacjach — ich koszy nie da się użyć,
  // zostaje wycofanie z oferty (archived).
  const usedPriceIds = React.useMemo(
    () =>
      new Set(
        (usage?.variants ?? []).flatMap((variant) =>
          variant.prices
            .filter((price) => price.used)
            .map((price) => price.priceId),
        ),
      ),
    [usage],
  );

  return (
    <>
      <ModalContent>
        <TwoInputsRow ratio="3/1">
          <TextInput
            label="Nazwa opcji dodatkowej"
            register={register('name')}
            placeholder="Wpisz nazwę opcji"
            required={true}
            error={errors.name?.message}
          />
          <SingleSelectInput
            fieldName="currency"
            placeholder="Wybierz"
            label="Waluta"
            required={true}
            control={control}
            options={currencySelectOptions}
            error={errors.currency?.message}
          />
        </TwoInputsRow>
        <TwoInputsRow>
          <CheckboxInput
            label="Obowiązkowa"
            register={register('required')}
            error={errors.required?.message}
          />
          <CheckboxInput
            label=" Aktywność posiada warianty"
            register={register('hasVariants')}
            error={errors.hasVariants?.message}
          />
        </TwoInputsRow>
        <TwoInputsRow ratio="2/1">
          <ToggleInput
            fieldPath="participantLimitMode"
            label="Limit rezerwacji"
            options={limitOptions}
            control={control}
          />
          <ValueInput
            label="Liczba miejsc"
            register={register('participantLimit', { valueAsNumber: true })}
            placeholder="0"
            sufix="osób"
            disabled={participationLimitModeValue === ParticipantLimitMode.NONE}
            error={errors.participantLimit?.message}
          />
        </TwoInputsRow>

        <TextInput
          label="Opis opcji dodatkowej"
          register={register('description')}
          placeholder="Wpisz opis opcji"
          error={errors.description?.message}
        />

        {isEdit && (
          <CheckboxInput
            label="Wycofana z oferty (archiwum)"
            hint="Opcja zniknie z formularza rezerwacji, ale pozostanie widoczna w istniejących rezerwacjach. Możesz ją przywrócić w każdej chwili."
            register={register('archived')}
            error={errors.archived?.message}
          />
        )}

        {!hasVariantsValue && (
          <>
            <TwoInputsRow ratio="1/1">
              <CheckboxInput
                label="Cena zależna od wieku"
                register={register('isPriceByAge')}
                error={errors.isPriceByAge?.message}
              />
              {isPriceByAgeValue && (
                <CheckboxInput
                  label="Dostępne tylko dla wybranych grup wiekowych"
                  register={register('isVariantOnlyForDefinedAgeRanges')}
                  error={errors.isVariantOnlyForDefinedAgeRanges?.message}
                />
              )}
            </TwoInputsRow>

            {!isPriceByAgeValue && (
              <TwoInputsRow ratio="1/2">
                <ValueInput
                  label="Cena podstawowa"
                  register={register('basePrice', { valueAsNumber: true })}
                  placeholder="0"
                  type="number"
                  step={0.01}
                  sufix={currencyValue}
                  error={errors.basePrice?.message}
                />
              </TwoInputsRow>
            )}
          </>
        )}
      </ModalContent>

      {!hasVariantsValue && isPriceByAgeValue && (
        <AgeRestrictionPriceForm
          currency={currencyValue}
          fieldArray={ageRestrictionPricesFieldArray}
          parentFieldName=""
          limitsEnabled={false}
          usedPriceIds={usedPriceIds}
        />
      )}

      {hasVariantsValue && (
        <div className="border-t border-neutral-200">
          <ModalContent>
            <div className="space-y-1">
              <h3 className="text-base font-semibold leading-[120%] text-base-text">
                Warianty
              </h3>
              <div className="text-sm text-neutral-600">
                Dodaj różne wersje opcji dodatkowej np. ski pass nocny, ski pass
                dzienny.
              </div>
            </div>

            <CheckboxInput
              label="Pozwalaj uczestnikom na wybór wielu wariantów"
              register={register('allowOptionMultiSelect')}
              error={errors.allowOptionMultiSelect?.message}
            />
          </ModalContent>
          <div className="space-y-2 bg-neutral-50 px-4 py-3">
            {fields.map((field, index) => (
              <TourVariantForm
                key={field.id}
                index={index}
                remove={remove}
                currency={currencyValue}
                usage={usage}
                usedPriceIds={usedPriceIds}
              />
            ))}
            <div>
              <ActionButton
                type="button"
                tone="outline"
                size="sm"
                onClick={() => append(emptyVariant)}
              >
                <Plus />
                Dodaj wariant
              </ActionButton>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TourOptionsForm;
